import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useCustomer } from 'autumn-js/react';
import CheckDialog from '@/components/autumn/check-dialog';
import {
  listMembersMembersGetOptions,
  inviteMemberMembersInvitePostMutation,
  revokeInvitationMembersRevokePostMutation,
  deleteMemberMembersUserIdDeleteMutation,
} from '@/api';
import { useAuth } from '@/lib/auth';

export const Route = createFileRoute('/_auth/members')({
  component: MembersPage,
});

const roleLabel = (role?: string | null) => {
  if (role === 'company_admin') return 'Owner';
  if (role === 'editor') return 'Editor';
  if (role === 'viewer') return 'Viewer';
  return role;
};

function MembersPage() {
  const { user } = useAuth();

  if (user?.role !== 'company_admin') {
    return <Navigate to="/dashboard" />;
  }

  return <MembersContent />;
}

function MembersContent() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(listMembersMembersGetOptions({}));
  const {
    check,
    customer,
    refetch: refetchCustomer,
    isLoading: isLoadingCustomer,
  } = useCustomer();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const refreshMembers = async () => {
    await refetchCustomer();
    queryClient.invalidateQueries({
      queryKey: listMembersMembersGetOptions({}).queryKey,
    });
  };

  const inviteMutation = useMutation({
    ...inviteMemberMembersInvitePostMutation(),
    onSuccess: async () => {
      toast.success('Invitation sent');
      setEmail('');
      await refreshMembers();
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      toast.error(
        detail === 'seats-exceeded'
          ? 'Your plan does not allow adding more seats'
          : detail || 'Failed to invite',
      );
    },
  });

  const revokeMutation = useMutation({
    ...revokeInvitationMembersRevokePostMutation(),
    onSuccess: async () => {
      toast.success('Invitation revoked');
      await refreshMembers();
    },
    onError: () => toast.error('Failed to revoke'),
  });

  const deleteMutation = useMutation({
    ...deleteMemberMembersUserIdDeleteMutation(),
    onSuccess: async () => {
      toast.success('Member removed');
      await refreshMembers();
    },
    onError: () => toast.error('Failed to remove'),
  });

  const seatLimit = customer?.features?.['5_seats']
    ? 5
    : customer?.features?.['2_seats']
      ? 2
      : 0;
  const members = data?.members ?? [];
  const invitations = data?.invitations ?? [];
  const usedSeats = members.length + invitations.length;

  const handleInvite = async () => {
    if (!email || isInviting) return;
    if (isLoadingCustomer || !customer) {
      toast.error('Billing info is still loading');
      return;
    }

    setIsInviting(true);
    try {
      if (seatLimit === 0) {
        await check({
          featureId: '2_seats',
          dialog: CheckDialog,
          withPreview: true,
        });
        return;
      }

      if (usedSeats >= seatLimit) {
        if (seatLimit === 2) {
          await check({
            featureId: '5_seats',
            dialog: CheckDialog,
            withPreview: true,
          });
        } else {
          toast.error('Your plan does not allow adding more seats');
        }
        return;
      }

      await inviteMutation.mutateAsync({ body: { email, role: 'editor' } });
    } finally {
      setIsInviting(false);
    }
  };

  const showYou =
    !isLoading && members.length === 0 && invitations.length === 0 && user;

  return (
    <div className="max-w-xl">
      <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-neutral-900 md:text-[30px]">
        Members
      </h1>
      <p className="mt-1 text-sm leading-6 text-neutral-600">
        Invite people to work on sessions with you.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleInvite();
        }}
        className="mt-6 flex items-center gap-2">
        <Input
          autoFocus
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          className="h-11 rounded-2xl px-4 text-base shadow-none md:text-base"
        />
        <Button
          type="submit"
          disabled={isInviting || !email}
          className="h-11 bg-[#AAE73D] px-5 text-[15px] text-black hover:bg-[#bff25a]">
          {isInviting ? 'Sending...' : 'Invite'}
        </Button>
      </form>

      <ul className="mt-8">
        {invitations.map((inv) => (
          <li
            key={`${inv.email}-${inv.invited_at}`}
            className="flex min-h-14 items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <div className="truncate text-[15px] font-medium text-neutral-900">
                {inv.email}
              </div>
              <div className="text-sm text-neutral-600">
                {roleLabel(inv.role)} · Pending
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              className="h-9 shrink-0 px-3 text-sm text-neutral-600"
              onClick={() =>
                revokeMutation.mutate({ body: { token: inv.token } })
              }
              disabled={revokeMutation.isPending}>
              Revoke
            </Button>
          </li>
        ))}

        {members.map((m) => (
          <li
            key={m.id}
            className="flex min-h-14 items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <div className="truncate text-[15px] font-medium text-neutral-900">
                {m.first_name} {m.last_name}
                {m.id === user?.id ? (
                  <span className="ml-2 text-sm font-normal text-neutral-600">
                    You
                  </span>
                ) : null}
              </div>
              <div className="truncate text-sm text-neutral-600">
                {m.email} · {roleLabel(m.role)}
              </div>
            </div>
            {m.id !== user?.id ? (
              <Button
                type="button"
                variant="ghost"
                className="h-9 shrink-0 px-3 text-sm text-neutral-600"
                onClick={() =>
                  deleteMutation.mutate({ path: { user_id: m.id } })
                }
                disabled={deleteMutation.isPending}>
                Remove
              </Button>
            ) : null}
          </li>
        ))}

        {showYou ? (
          <li className="flex min-h-14 items-center py-2">
            <div className="min-w-0">
              <div className="truncate text-[15px] font-medium text-neutral-900">
                {user.first_name} {user.last_name}
                <span className="ml-2 text-sm font-normal text-neutral-600">
                  You
                </span>
              </div>
              <div className="truncate text-sm text-neutral-600">
                {user.email} · {roleLabel(user.role)}
              </div>
            </div>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
